import {
  DeclinedActionDto,
  GetIssueResponseDto,
} from '@issuemanager/api-client';
import {
  ControlledFileUploader,
  ControlledTextField,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledDivider,
} from '@issuemanager/apps/client/components';
import {
  useAllMutations,
  useAllQueries,
  useMonitorDeclineIssueLoad,
  useMonitorDeclineIssueSubmit,
} from '@issuemanager/apps/client/hooks';
import { useDeclineIssue } from '@issuemanager/client/features/issues';
import { useUsers } from '@issuemanager/client/features/users';
import {
  DECLINE_REASON_LENGTH_MAX,
  DECLINE_REASON_LENGTH_MIN,
  IssuePermissionsEnum,
} from '@issuemanager/constants';
import { Checkbox, Stack, Typography, useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  RegisterOptions,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LayoutPanel } from '../layout';

type FormValues = {
  actions: {
    id: string;
    reason: string;
    checked: boolean;
    attachments: DeclinedActionDto['attachments'];
  }[];
};

type LocalFields = 'reason';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  code: GetIssueResponseDto['code'];
  actions: GetIssueResponseDto['actions'];
};

export function IssueDeclinePanel(props: Props) {
  const { isOpen, onClose, code, actions } = props;
  const { t } = useTranslation('issue');
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState<number[]>([0]);

  const monitorDeclineIssueLoad = useMonitorDeclineIssueLoad();
  const users = useUsers({ enabled: isOpen, handleError: true });

  useAllQueries({
    queries: [users],
    onStart: () => {
      monitorDeclineIssueLoad.start();
    },
    onSuccess: () => {
      monitorDeclineIssueLoad.stop();
    },
    onError: () => {
      monitorDeclineIssueLoad.error();
    },
  });

  const declineIssue = useDeclineIssue({
    code,
    onSuccess: () =>
      enqueueSnackbar(t('manage.decline.success'), {
        variant: 'success',
      }),
  });

  const monitorDeclineIssueSubmit = useMonitorDeclineIssueSubmit();

  useAllMutations({
    mutations: [declineIssue],
    onStart: () => {
      monitorDeclineIssueSubmit.start();
    },
    onSuccess: () => {
      monitorDeclineIssueSubmit.stop();
    },
    onError: () => {
      monitorDeclineIssueSubmit.error();
    },
  });

  const form = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      actions: [],
    },
  });

  const actionsField = useFieldArray({
    name: 'actions',
    control: form.control,
  });

  const handleExpand = useCallback(
    (index: number) => {
      if (expanded.includes(index)) {
        setExpanded(expanded.filter((item) => item !== index));
      } else {
        setExpanded([...expanded, index]);
      }
    },
    [expanded],
  );

  const { watch } = form;
  const actionsValues = watch('actions');
  const checkedActions = actionsValues.filter((action) => action.checked);
  const allCheckedActionsHaveReason = checkedActions.every(
    (action) => action.reason.length > 0,
  );
  const actionButtonProps = {
    disabled: !(checkedActions.length && allCheckedActionsHaveReason),
    'data-testid': 'decline-footer-button',
  };

  const rules: Record<LocalFields, RegisterOptions<FormValues>> = {
    reason: {
      maxLength: {
        value: DECLINE_REASON_LENGTH_MAX,
        message: t('manage.decline.errors.reason.maxLength'),
      },
      minLength: {
        value: DECLINE_REASON_LENGTH_MIN,
        message: t('manage.decline.errors.reason.minLength'),
      },
    },
  };

  const handleSubmit = useEventCallback(() => {
    form.handleSubmit(onSubmit)();
  });

  const onSubmit = async ({ actions }: FormValues) => {
    await declineIssue.mutateAsync({
      declinedActions: actions
        .filter((action) => action.checked)
        .map((action) => ({
          actionId: action.id,
          reason: action.reason,
          attachments: action.attachments,
        })),
    });
    onClose();
  };

  const arrayFieldFields = actionsField.fields.map((field, index) => ({
    id: field.id,
    reason: (
      <ControlledTextField
        name={`actions.${index}.reason`}
        label={t('manage.decline.labels.reason')}
        placeholder={t('manage.decline.placeholder')}
        control={form.control}
        rules={rules.reason}
        data-testid='decline-action-reason'
        multiline
        minRows={6}
        maxLength={DECLINE_REASON_LENGTH_MAX}
      />
    ),
    checked: (
      <Controller
        name={`actions.${index}.checked`}
        control={form.control}
        render={({ field: { value, onChange } }) => (
          <Checkbox
            onChange={(e) => {
              onChange(e.currentTarget.checked);
              handleExpand(index);
            }}
            checked={Boolean(value)}
            sx={{ marginTop: '3px' }}
            data-testid='decline-action-checkbox'
          />
        )}
      />
    ),
    attachments: (
      <ControlledFileUploader
        name={`actions.${index}.attachments`}
        control={form.control}
        label={t('manage.decline.labels.attachments')}
        permissions={[IssuePermissionsEnum.IsApprover]}
      />
    ),
  }));

  useEffect(() => {
    form.reset({
      actions:
        actions?.map((action, index) => ({
          id: action.id,
          reason: '',
          checked: index === 0,
          attachments: [],
        })) ?? [],
    });
  }, [actions, form]);

  return (
    <LayoutPanel
      isOpen={isOpen}
      onCancel={() => onClose()}
      title={t('manage.decline.title')}
      description={t('manage.decline.description')}
      actionButton={actionButtonProps}
      actionButtonText={
        declineIssue.isPending
          ? t('manage.decline.declining')
          : t('manage.decline.accept')
      }
      isLoading={declineIssue.isPending}
      onSend={handleSubmit}
      content={
        <FormProvider {...form}>
          <form data-testid='form-decline-issue'>
            <Stack gap={1} flex={1} flexWrap='wrap'>
              {arrayFieldFields.map((field, index) => (
                <>
                  <StyledAccordion
                    data-atlas-alignment='end'
                    key={field.id}
                    data-testid='issue-decline-action'
                    data-index={index}
                    expanded={expanded.includes(index)}
                  >
                    <StyledAccordionSummary
                      aria-controls={`panel${field.id}-content`}
                      id={`panel${field.id}-header`}
                      expandIcon={null}
                    >
                      <Stack
                        flexDirection='row'
                        width='100%'
                        justifyContent='space-between'
                      >
                        <Typography variant='h4' fontWeight='600'>
                          {`${t('manage.decline.section')} ${index + 1}`}
                        </Typography>
                        {field.checked}
                      </Stack>
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>
                      <Stack width='100%' spacing={2} mb={4}>
                        {field.reason}
                        {field.attachments}
                      </Stack>
                    </StyledAccordionDetails>
                  </StyledAccordion>
                  <StyledDivider flexItem sx={{ m: 0 }} />
                </>
              ))}
            </Stack>
          </form>
        </FormProvider>
      }
    />
  );
}
